import { Flex, rem, Skeleton } from '@mantine/core';
import { personDetailsPath } from '@dealstack/frontend/app/pages/Routing/routes.utils.ts';
import { GetPersonByIdRes } from '@dealstack/shared/lib/generated';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { InvestorDetailsCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/InvestorDetailsCard/InvestorDetailsCard.tsx';
import { InvestorDetailsRow } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/InvestorDetailsRow/InvestorDetailsRow.tsx';
import {
	getJurisdictionName,
	getLegalForm,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestmentVehicleDetails/components/VehicleProfileCard/vehicleProfileCard.utils.ts';

interface Props {
	dealId: string;
	vehicle: GetPersonByIdRes | undefined;
	isLoading?: boolean;
}

export const VehicleProfileCard = ({ dealId, vehicle, isLoading = false }: Props) => {
	const tMipManage = useTranslationMipManage();

	if (isLoading) {
		return <Skeleton height={rem(232)} radius="0.5rem" />;
	}

	const registeredName = vehicle?.registrationDetails?.name?.trim() || vehicle?.displayName;
	const registrationNumber = vehicle?.registrationDetails?.corporateRegistrationNumber?.trim();
	const jurisdiction = getJurisdictionName(vehicle?.registrationDetails);
	const legalForm = getLegalForm(vehicle?.registrationDetails);

	const directoryProfilePath = vehicle?.id
		? personDetailsPath({ dealId, personId: vehicle.id })
		: undefined;

	return (
		<InvestorDetailsCard
			title={tMipManage('investors.details.profile_card.title')}
			action={directoryProfilePath ? { kind: 'link', to: directoryProfilePath } : undefined}
			dataTestId={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTMENT_VEHICLE_DETAILS_PROFILE_CARD}
		>
			<Flex direction="column" gap="0.25rem">
				<InvestorDetailsRow
					label={tMipManage('investors.details.vehicle_profile_card.registered_name', {
						defaultValue: 'Registered name',
					})}
					value={registeredName}
					copyValue={registeredName}
					showValueTooltip
					dataTestId={
						TEST_ELEMENT_IDS.MIP_MANAGE_INVESTMENT_VEHICLE_DETAILS_REGISTERED_NAME
					}
				/>

				<InvestorDetailsRow
					label={tMipManage(
						'investors.details.vehicle_profile_card.registration_number',
						{ defaultValue: 'Registration number' }
					)}
					value={registrationNumber}
					copyValue={registrationNumber}
					dataTestId={
						TEST_ELEMENT_IDS.MIP_MANAGE_INVESTMENT_VEHICLE_DETAILS_REGISTRATION_NUMBER
					}
				/>

				<InvestorDetailsRow
					label={tMipManage('investors.details.vehicle_profile_card.jurisdiction', {
						defaultValue: 'Jurisdiction',
					})}
					value={jurisdiction}
					showValueTooltip
					dataTestId={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTMENT_VEHICLE_DETAILS_JURISDICTION}
				/>

				<InvestorDetailsRow
					label={tMipManage('investors.details.vehicle_profile_card.legal_form', {
						defaultValue: 'Legal form',
					})}
					value={legalForm?.label}
					valueTooltip={legalForm?.tooltip}
					dataTestId={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTMENT_VEHICLE_DETAILS_LEGAL_FORM}
				/>
			</Flex>
		</InvestorDetailsCard>
	);
};
